import {SelectItem} from '@components/inputs/select/Select.component';
import {Colors} from '@constants/colors.constants';
import {FacturationRuleType} from '@store/clients/details/client-details.enums';
import {Address} from '@store/common/address.model';
import {PaymentTerm, TaxRule} from '@store/common/organisation.enums';
import {Pagination} from '@store/common/pagination.model';
import {TableFilter, TableFilterType} from '@store/common/tableFilter.model';
import {ExecutionLine} from '@store/invoices/executionLines';
import I18n from '@utils/i18n.utils';

export enum InvoiceType {
  DEFAULT = 'DEFAULT',
  CANCEL = 'CANCEL',
  CANCELLED = 'CANCELLED',
}

export namespace InvoiceType {
  export const getColor = (invoiceType: InvoiceType) => {
    switch (invoiceType) {
      case InvoiceType.DEFAULT:
        return Colors.lightGrey;
      case InvoiceType.CANCEL:
        return Colors.warning;
      case InvoiceType.CANCELLED:
        return Colors.error;
    }
  };

  export const getLabel = (invoiceType: InvoiceType) => {
    switch (invoiceType) {
      case InvoiceType.DEFAULT:
        return 'global.invoiceType.DEFAULT';
      case InvoiceType.CANCEL:
        return 'global.invoiceType.CANCEL';
      case InvoiceType.CANCELLED:
        return 'global.invoiceType.CANCELLED';
    }
  };
}

export enum InvoiceStatus {
  UNKNOWN = 'NON DEFINI',
  DRAFT = 'DRAFT',
  VALIDATED = 'VALIDATED',
  SENT = 'SENT',
  PAID = 'PAID',
  LATE = 'LATE',
}

export namespace InvoiceStatus {
  export const selectItems = () => [
    {
      label: I18n.t('global.billsEnum.DRAFT'),
      value: InvoiceStatus.DRAFT,
      backgroundColor: Colors.progress,
    },
    {
      label: I18n.t('global.billsEnum.VALIDATED'),
      value: InvoiceStatus.VALIDATED,
      backgroundColor: Colors.validated,
    },
    {
      label: I18n.t('global.billsEnum.SENT'),
      value: InvoiceStatus.SENT,
      backgroundColor: Colors.sent,
    },
    {
      label: I18n.t('global.billsEnum.PAID'),
      value: InvoiceStatus.PAID,
      backgroundColor: Colors.paid,
    },
    {
      label: I18n.t('global.billsEnum.LATE'),
      value: InvoiceStatus.LATE,
      backgroundColor: Colors.late,
    },
  ];
}

export enum InvoiceTypology {
  RETAIL = 'RETAIL',
  INDUSTRIES = 'INDUSTRIES',
  CONSTRUCTION = 'CONSTRUCTION',
  SMB = 'SMB',
  MATERIAL_SUPPLIER = 'MATERIAL_SUPPLIER',
  TRANSPORT = 'TRANSPORT',
  RECYCLING = 'RECYCLING',
  VALORISATION = 'VALORISATION',
}

export namespace InvoiceTypology {
  export const selectItems = () =>
    [
      InvoiceTypology.RETAIL,
      InvoiceTypology.INDUSTRIES,
      InvoiceTypology.CONSTRUCTION,
      InvoiceTypology.SMB,
      InvoiceTypology.MATERIAL_SUPPLIER,
      InvoiceTypology.TRANSPORT,
      InvoiceTypology.RECYCLING,
      InvoiceTypology.VALORISATION,
    ].map((it) => ({label: I18n.t(`global.billTypologyEnum.${it}`), value: it}));
}

export interface BillsGroupedAction {
  status?: InvoiceStatus;
}

export interface InvoiceForMailing {
  id: number;
  invoice: Invoice;
  emails: string[];
  ccEmails: string[];
  address: string;
}

export enum SendInvoiceType {
  GROUPED_BY_EMAIL = 'GROUPED_BY_EMAIL',
  ONE_BY_EMAIL = 'ONE_BY_EMAIL',
}

export namespace SendInvoiceType {
  export const selectItems = () => [
    {
      label: I18n.t('global.invoiceType.GROUPED_BY_EMAIL'),
      value: SendInvoiceType.GROUPED_BY_EMAIL,
    },
    {
      label: I18n.t('global.invoiceType.ONE_BY_EMAIL'),
      value: SendInvoiceType.ONE_BY_EMAIL,
    },
  ];
}

export interface SendInvoiceForMailing {
  id: number;
  emails: string[];
  ccEmails: string[];
}

export interface InvoiceCreationBase {
  from: Date;
  to: Date;
  clientIds: number[];
  clientUnitIds: number[];
  typologies: InvoiceTypology[];
  documentDate: Date;
  reminderDate: Date;
  invoicingRule: FacturationRuleType;
  includeAdminFee?: boolean;
}

export type InvoiceCreation = InvoiceCreationBase & {
  clientItems: SelectItem[];
  clientUnitItems: SelectItem[];
  typologyItems: SelectItem[];
  invoicingRuleItem: SelectItem;
}

export interface InvoiceClient {
  id: number;
  address: Address;
  clientName: string;
  clientUnitName: string;
  country: string;
  phone: string;
  currency: string;
}

export interface InvoiceSupplier {
  id: number;
  address: Address;
  supplierName: string;
  supplierUnitName: string;
  country: string;
  phone: string;
  currency: string;
}

export interface InvoiceDetails {
  id: number;
  clientId: number;
  status: InvoiceStatus;
  comment: string;
  wireToAccountName: string;
  wireToAccountAddress: string;
  wireToAccountZip: string;
  wireToAccountCity: string;
  dueDate: Date;
  createdAt: Date;
  documentDate: Date;
  reminderDate: Date;
  invoiceRef: string;
  clientName: string;
  totalAmountNoVatInCents: number;
  totalAmountVatIncludedInCents: number;
  facturationType: string;
  taxRule: TaxRule;
  paymentTermNumDays: PaymentTerm;
  clients: InvoiceClient[];
  clientInvoiceLines: ExecutionLine[];
  accountingTransferDate?: Date;
  currency: string;
  invoiceOriginId: number;
  type: InvoiceType;
  cancellationReason: string;
  changeRate?: number;
  totalAmountVatIncludedInCentsCHF?: number;
}

export interface InvoicesFilters {
  filterAccountantTransferred: boolean;
  search: string;
  statuses?: InvoiceStatus[];
  tableFilters: TableFilter[];
  from?: Date;
  to?: Date;
}

export namespace InvoicesFilters {
  export const initTableFilters = () =>
    TableFilterType.initTableFilters([
      TableFilterType.CLIENT_ORGANISATION,
      TableFilterType.CLIENT_ORGANISATION_UNIT,
      TableFilterType.ORGANISATION_TYPOLOGY,
    ]);
}

export interface InvoicesPagination extends Pagination {
  data: Invoice[];
  sumNoVatInCents: number;
}

export interface InvoiceUI {
  id: number;
  selected: boolean;
}

export interface Invoice {
  id: number;
  invoiceRef: string;
  createdAt: Date;
  clientCount: number;
  unitCount: number;
  accountingTransferDate: string;
  clientName: string;
  unitName: string;
  facturationType: string;
  from: string;
  to: string;
  comment: string;
  totalAmountNoVatInCents: number;
  totalAmountVatIncludedInCents: number;
  currency: string;
  status: InvoiceStatus;
  organisationKind: string;
  deletedAt: Date;
  type: InvoiceType;
  documentDate: Date;
  invoiceDate: Date;
  cancelledCount: number;
  cancelledAmountNoVatInCents: number;

}
